import { css } from '@emotion/react';
import { icons, SvgIcon, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { ReactNode } from 'react';
import { ShortRule } from 'shared';

const styles = {
  shortRule: (iconPlacement: 'top' | 'left') => css`
    color: ${tokens.color.gray['20']};
    flex: 1;
    display: flex;
    align-items: ${iconPlacement === 'top' ? 'center' : 'start'};
    gap: ${tokens.dimension['space-2']};
    flex-direction: ${iconPlacement === 'top' ? 'column' : 'row'};
  `,
  shortRuleText: (largeFont?: boolean) => css`
    margin: 0;
    font-size: ${largeFont ? tokens.fontSize.paragraph['paragraph-2'] : tokens.fontSize.paragraph['paragraph-1']};
  `,
  shortRuleBody: (iconPlacement: 'top' | 'left') => css`
    margin-top: 3px;
    text-align: ${iconPlacement === 'top' ? 'center' : 'left'};
  `,
  rebookRefundContainer: css`
    display: flex;
    flex-direction: row;
    padding: 0;
  `,
  listWithGap: (gap: keyof typeof tokens.dimension) => css`
    list-style: none;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${tokens.dimension[gap]};
    padding: 0;
    li {
      list-style: none;
      padding: 0;
      margin: 0;
    }
  `,
  baggageContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-1']};
    margin: 0;
    padding: 0;
  `,
  diemensions: css`
    color: ${tokens.color.gray['40']};
    font-size: ${tokens.fontSize.paragraph['paragraph-1']};
  `,
  typeOfBaggage: css`
    color: ${tokens.color.gray['20']};
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
  `,
  svgIcon: css`
    flex-shrink: 0;
  `,
};

type IconId = keyof typeof icons;

const getRuleIcon = (shortRule: ShortRule): IconId =>
  shortRule.iconId in icons ? (shortRule.iconId as IconId) : 'Check';

export function RebookConditionShortRuleList({ shortRules }: { shortRules: ShortRule[] }) {
  return (
    <ShortRuleList
      as="ul"
      aria-label={t('common:conditions', { defaultValue: 'Conditions' })}
      css={styles.rebookRefundContainer}
    >
      {shortRules.map(rule => {
        const { text, id } = rule;
        return (
          <ShortRuleItem
            as="li"
            css={styles.listWithGap('space-2')}
            key={id}
            iconPlacement="top"
            icon={getRuleIcon(rule)}
          >
            {text}
          </ShortRuleItem>
        );
      })}
    </ShortRuleList>
  );
}

export function BaggageShortRuleList({ shortRules }: { shortRules: ShortRule[]; largeFont?: boolean }) {
  return (
    <ShortRuleList as="ul" aria-label={t('common:included')} css={styles.listWithGap('space-5')}>
      {shortRules.map(rule => {
        const [typeOfBaggage, baggageDimensions] = rule?.text.split(/[()]/).filter(part => part.trim() !== '') ?? [];
        return (
          <ShortRuleItem as="li" key={rule.id} icon={getRuleIcon(rule)}>
            <p css={styles.baggageContainer}>
              <span css={styles.typeOfBaggage}>{typeOfBaggage}</span>
              {baggageDimensions && <span css={styles.diemensions}>{baggageDimensions}</span>}
            </p>
          </ShortRuleItem>
        );
      })}
    </ShortRuleList>
  );
}

export function OrdinaryShortRules({ shortRules, className }: { shortRules: ShortRule[]; className?: string }) {
  return (
    <ShortRuleList as="div" className={className} css={styles.listWithGap('space-2')}>
      {shortRules.map(rule => (
        <ShortRuleItem as="div" key={rule.id} icon={'Check'} iconPlacement="left">
          {rule.text}
        </ShortRuleItem>
      ))}
    </ShortRuleList>
  );
}

const ShortRuleItem = ({
  icon,
  iconPlacement = 'left',
  as: AsElement,
  children,
  className,
}: {
  iconPlacement?: 'top' | 'left';
  icon: keyof typeof icons;
  children?: ReactNode;
  className?: string;
  as: React.ElementType<{
    className?: string;
    children?: ReactNode;
  }>;
}) => (
  <AsElement className={className} css={styles.shortRuleText(iconPlacement === 'top')}>
    <ShortRuleParagraph icon={icon} iconPlacement={iconPlacement}>
      {children}
    </ShortRuleParagraph>
  </AsElement>
);

const ShortRuleList = ({
  'aria-label': ariaLabel,
  children,
  as: AsElement,
  className,
}: {
  'aria-label'?: string;
  children?: ReactNode;
  className?: string;
  as: React.ElementType<{
    className?: string;
    children?: ReactNode;
  }>;
}) => (
  <AsElement aria-label={ariaLabel} className={className}>
    {children}
  </AsElement>
);

const ShortRuleParagraph = ({
  iconPlacement = 'left',
  icon,
  children,
}: {
  iconPlacement?: 'top' | 'left';
  icon: keyof typeof icons;
  children?: React.ReactNode;
}) => (
  <div css={styles.shortRule(iconPlacement)}>
    <SvgIcon ariaHidden css={styles.svgIcon} icon={icon} size={iconPlacement === 'top' ? 'small' : 'xs'} />
    <div css={styles.shortRuleBody(iconPlacement)}>{children}</div>
  </div>
);
